import { Component, NgZone, OnInit, computed, inject, signal } from '@angular/core';
import { AuthenticatorService, AmplifyAuthenticatorModule } from '@aws-amplify/ui-angular';
import { AmplifyService } from './services/amplify.service';
import { LoggingService } from './services/logging.service';
import { Router, RouterLink, RouterOutlet } from '@angular/router';
import { Entities, Permissions, PermissionsService } from './services/permissions.service';
import { GroupsService } from './services/groups.service';
import { UtilitiesService } from './services/utilities.service';
import { FormsModule } from '@angular/forms';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { NgClass } from '@angular/common';
import { fetchUserAttributes, signIn } from 'aws-amplify/auth';
import { take } from 'rxjs';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  imports: [
    AmplifyAuthenticatorModule,
    NzLayoutModule,
    RouterLink,
    NzMenuModule,
    NzIconModule,
    NgClass,
    NzRadioModule,
    FormsModule,
    RouterOutlet,
  ],
})
export class AppComponent implements OnInit {
  public async ngOnInit(): Promise<void> {
    this.locale.locale = 'en';
    if (!localStorage.getItem('USER_LOCALE')) {
      if (this.locale.locale !== localStorage.getItem('USER_LOCALE')) {
        this.locale.locale = localStorage.getItem('USER_LOCALE') ?? this.locale.locale;
        this.switchLanguage();
      } else {
        localStorage.setItem('USER_LOCALE', this.locale.locale);
      }
    }

    this.authenticatorService.subscribe((authenticator) => {
      this.amplifyService.signedIn(authenticator.authStatus === 'authenticated');

      if (authenticator.authStatus === 'authenticated') {
        fetchUserAttributes().then((attributes) => {
          this.userEmail = attributes.email;
        });

        this.permissionsService.loadPermissions().pipe(take(1)).subscribe();
        this.groupsService.getUserGroups();
      }
    });
  }

  public services(that: AppComponent): { handleSignIn(formData: Record<string, any>): Promise<any> } {
    return {
      async handleSignIn(formData: Record<string, any>): Promise<any> {
        const { username, password } = formData;
        const user = await signIn({
          username,
          password,
        });

        that.permissionsService.loadPermissions().pipe(take(1)).subscribe();

        return user;
      },
    };
  }

  public signOut(): void {
    this.amplifyService.signingOut();
  }

  public switchLanguage(): void {
    localStorage.setItem('USER_LOCALE', this.locale.locale);

    if (this.locale.locale === 'es') {
      window.location.replace('https://' + window.location.host + '/es');
    } else if (this.locale.locale === 'en') {
      window.location.replace('https://' + window.location.host + '/en');
    } else {
      window.location.replace('https://' + window.location.host + '/fr');
    }
  }

  public redirectTo(event: Event, uri: string): void {
    event.preventDefault();
    event.stopImmediatePropagation();
    this.ngZone.run(() => {
      this.router
        .navigateByUrl(uri)
        .then()
        .catch((error) => {
          this.logger.error(error);
        });
    });
  }

  public toggleCollapse(): void {
    this.isMenuCollapse.update((value) => !value);
  }

  private readonly utilitiesService = inject(UtilitiesService);
  private readonly authenticatorService = inject(AuthenticatorService);
  private readonly amplifyService = inject(AmplifyService);
  private readonly logger = inject(LoggingService);
  private readonly ngZone = inject(NgZone);
  private readonly router = inject(Router);
  private readonly permissionsService = inject(PermissionsService);
  private readonly groupsService = inject(GroupsService);

  public hasDomainValuesMenu = this.permissionsService.can(Permissions.Read, Entities.DomainValues);
  public hasUsersMenu = this.permissionsService.can(Permissions.Read, Entities.Users);
  public hasPermissionsMenu = this.permissionsService.can(Permissions.Read, Entities.Permissions);
  public hasOrganizationsMenu = this.permissionsService.can(Permissions.Read, Entities.Organizations);
  public hasInvestmentProjectsMenu = this.permissionsService.can(Permissions.Read, Entities.InvestmentProjects);
  public isMenuCollapse = signal<boolean>(false);
  public locale = this.utilitiesService.locale();
  public isSignedIn = computed(() => this.amplifyService.isSignedIn());
  public userEmail?: string;
  public formFields = {
    signIn: {
      username: {
        labelHidden: false,
        placeholder: $localize`Enter your email`,
        isRequired: true,
      },
      password: {
        labelHidden: false,
        placeholder: $localize`Enter your password`,
        isRequired: true,
      },
    },
  };
}
